import {
  ImportModuleIncompleteCard, ImportModuleKulturnavRef, ImportModuleLackingRight, ImportModuleMediaPatternError,
  ImportModulePreflightResult,
  ImportModulePreflightRow,
} from '../../../core/definitions/import-module';
import {headerOptionName} from '../group-card/group-card-title';
import {
  GroupCardDescriptor, GroupCardModel, GroupKind, GroupMemberRow, memberLabelInCard,
} from '../group-card/group-card.model';
import {CREATED_AT_TARGET, CREATED_BY_TARGET, IDENTIFICATION_NUMBER_TARGET, memberFixedIsPick} from '../import-payload';
import {kulturnavRefText} from '../kulturnav-ref';

type Composed = ImportModulePreflightRow['composed'][number];

/** What a line or member row gets in the previewed row: its values, `[]`
 * when it gets none, or `null` when the answer cannot tell. */
export type RowValues = readonly string[] | null;

/** A label: a text, or a translation key and the card's number. */
export interface PreviewLabel {
  text?: string;
  key?: string;
  index?: number;
}

/** The heading of the mapping list a line sits under. */
export interface PreviewSectionRef {
  key: string;
  titleKey: string;
}

/** A flat row that sends a mapping entry. `line` is the row itself, and
 * `order` its place among the flat rows' entries as they go out, which
 * need not be the order the list shows the rows in. `onMedia`: the row
 * writes onto the linked media, and `order` counts among those rows. */
export interface PreviewFlatLine {
  kind: 'flat';
  line: object;
  fieldId: string;
  label: string;
  order: number;
  // The row takes its value through a lookup.
  readsLookup: boolean;
  section: PreviewSectionRef;
  onMedia: boolean;
}

/** The title of the values written onto the linked media, «Bilde». */
export const MEDIA_VALUES_TITLE_KEY = 'TRANS__SUPEROBJECT_TYPE_IMAGE';

/** The preview's section for the values written onto the linked media. */
export const MEDIA_VALUES_SECTION: PreviewSectionRef = {key: 'media_values', titleKey: MEDIA_VALUES_TITLE_KEY};

/** How the line of a Named or Place sub-block's member row is labelled on
 * the preview: by the sub-block's role, else by what kind of sub-block it
 * is, with its role status after the value. `key` is its `sub_block`. */
export interface PreviewSubBlock {
  key: string;
  label: PreviewLabel;
  detail: string | null;
}

/** A group card with member rows that send a mapping entry, `rows` in the
 * order their entries go out; `index` counts from 1 among its kind. A card
 * with a `title` is labelled by it, any other by its kind and `index`.
 * `header` holds its header picks that the title does not already name,
 * `subBlocks` the sub-block each sub-block member among `rows` belongs to,
 * and `pickNames` the name of the record a fixed pick among `rows` picks,
 * where known. */
export interface PreviewCardLine {
  kind: 'card';
  line: GroupCardModel;
  groupKind: GroupKind;
  descriptor: GroupCardDescriptor;
  index: number;
  title?: string;
  rows: readonly GroupMemberRow[];
  // The member rows that take their value through a lookup.
  lookupRows: ReadonlySet<GroupMemberRow>;
  section: PreviewSectionRef;
  header: readonly PreviewItem[];
  subBlocks: ReadonlyMap<GroupMemberRow, PreviewSubBlock>;
  pickNames: ReadonlyMap<GroupMemberRow, string>;
}

export type PreviewLine = PreviewFlatLine | PreviewCardLine;

/** A card's values in the previewed row, and each member row's. */
export interface CardRowValues {
  values: RowValues;
  members: ReadonlyMap<GroupMemberRow, RowValues>;
}

/** One value on the preview. `path` says where in a hierarchic list a
 * concept value lands - its term's ancestors and name - when that says
 * more than the value. `kulturnav` is set on a KulturNav objectid that
 * has a local record or a page to link to; `text` is then what the link
 * reads. */
export interface PreviewValue {
  text: string;
  path?: string;
  kulturnav?: ImportModuleKulturnavRef;
}

/** A labelled line of a card on the preview: a member's, a header pick's
 * or a sub-block's, with what to say after the values. */
export interface PreviewItem {
  label: PreviewLabel;
  values: readonly PreviewValue[];
  detail?: string | null;
}

/** One field or card on the preview: a field with its values, or a card
 * with its labelled lines. */
export interface PreviewEntry {
  label: PreviewLabel;
  values: readonly PreviewValue[];
  items: readonly PreviewItem[];
  // Takes the whole width of the preview: several values or lines, a long
  // value, or a value with its place in a hierarchy.
  wide: boolean;
}

/** The entries under one of the mapping list's headings; `titleKey` is
 * `null` for the values no line takes. */
export interface PreviewSection {
  key: string;
  titleKey: string | null;
  entries: readonly PreviewEntry[];
}

/** The answer's block-level errors that bear on the preview: the target
 * fields of mapping lines it could not resolve, every other message, the
 * cards that can never write anything, the rights the user lacks, and
 * why the media match pattern is refused, `null` when it is not. */
export interface PreviewErrors {
  fields: readonly string[];
  messages: readonly string[];
  incompleteCards: readonly ImportModuleIncompleteCard[];
  lackingRights: readonly ImportModuleLackingRight[];
  mediaPattern: ImportModuleMediaPatternError | null;
}

/**
 * Row `row` as the object it becomes, and what each mapping line gets from
 * it. `found` is false when the table has no such row. `identifier` is the
 * identification number, and `title` the first title card's title with
 * its other values; neither is listed again in `sections`. `empty` names
 * the lines the row gives nothing.
 */
export interface LivePreviewView {
  row: number;
  found: boolean;
  errors: PreviewErrors;
  identifier: string | null;
  title: {text: string; details: readonly string[]} | null;
  sections: readonly PreviewSection[];
  empty: readonly PreviewLabel[];
  lines: ReadonlyMap<object, RowValues>;
  cards: ReadonlyMap<GroupCardModel, CardRowValues>;
}

/** A value longer than this takes the whole width of the preview. */
export const WIDE_VALUE_LENGTH = 24;

/** The key of the section that lists the values no line takes. */
export const UNPLACED_SECTION_KEY = 'unplaced';

/** What a line or member row gets from the answer: its entries, `[]` for
 * none, `null` when the answer cannot tell. */
type Slot = readonly Composed[] | null;

/**
 * What the one-row preflight `result` for row `row` shows on `lines`, the
 * mapping lines in the order the list shows them, under the list's
 * headings.
 *
 * A card's values are found by its key (`event_key`), and among them a
 * sub-block's by its `sub_block` and any other member row's by its target
 * field. A flat row's are the values of its field that name no card, a
 * row onto the linked media's those the answer marks `on: 'media'` and any
 * other row's those it does not; the identification number and the
 * created date and user come from their own parts of the answer. Rows onto
 * the same field get the values in order
 * when there are as many values as rows, none when there is no value, and
 * are unknown otherwise. A fixed pick shows by its record's name where
 * the card's `pickNames` knows it. A value no line takes is listed by its
 * own label, one written onto the linked media under «Bilde».
 *
 * A block-level error makes what it touches unknown rather than empty: a
 * target field the answer could not resolve, its lines; a group error,
 * the cards; a lookup error, whose values are then read without their
 * lookup, the rows reading a lookup, values and all; a media error or a
 * refused media match pattern, whose answer then composes no value onto
 * the linked media, the rows onto it; an error that names no line's field, every line; the
 * created-revision right the user lacks, whose answer then previews no
 * created date or user, those two lines. A card that can never write
 * (`incompleteCards`) and any other lacking right make nothing unknown:
 * the answer still previews their values.
 */
export function buildLivePreviewView(
  row: number, result: ImportModulePreflightResult, lines: readonly PreviewLine[],
): LivePreviewView {
  const errors = previewErrors(result, lines);
  const answer = result.rows[0];
  if (!answer) {
    return {
      row, found: false, errors, identifier: null, title: null, sections: [], empty: [], lines: new Map(),
      cards: new Map(),
    };
  }
  const unread = new Set(answer.composed);
  const take = (match: (entry: Composed) => boolean): Composed[] => {
    const taken = answer.composed.filter(entry => unread.has(entry) && match(entry));
    taken.forEach(entry => unread.delete(entry));
    return taken;
  };
  const doubt = errorDoubt(result, errors);
  const flatLines = lines.filter((line): line is PreviewFlatLine => line.kind === 'flat');
  const cardLines = lines.filter((line): line is PreviewCardLine => line.kind === 'card');

  const cardParts = new Map<PreviewCardLine, CardParts>();
  for (const card of cardLines) {
    cardParts.set(card, readCard(card, take(entry => entry.event_key === card.line.key), doubt));
  }

  const flatSlots = new Map<PreviewFlatLine, Slot>();
  for (const same of groupBy(flatLines, line => JSON.stringify([line.fieldId, line.onMedia])).values()) {
    const {fieldId, onMedia} = same[0];
    const own = onMedia ? undefined : ownPartEntries(answer, fieldId);
    if (own !== undefined) {
      same.forEach(line => flatSlots.set(line, own));
      continue;
    }
    const slots = inOrder(same.length, take(entry =>
      !entry.event_key && entry.target_field === fieldId && (entry.on === 'media') === onMedia));
    byOrder(same, line => line.order).forEach((line, i) => flatSlots.set(line, slots[i]));
  }
  for (const line of flatLines) {
    flatSlots.set(line, doubt(flatSlots.get(line)!, line.fieldId, line.readsLookup, line.onMedia ? 'media' : 'object'));
  }

  const lineValues = new Map<object, RowValues>();
  const cards = new Map<GroupCardModel, CardRowValues>();
  flatSlots.forEach((slot, line) => lineValues.set(line.line, slot && slot.map(entry => entry.value)));
  cardParts.forEach((parts, card) => {
    cards.set(card.line, parts.values);
    lineValues.set(card.line, parts.values.values);
  });

  const sections: {key: string; titleKey: string | null; entries: PreviewEntry[]}[] = [];
  const entriesOf = (ref: PreviewSectionRef): PreviewEntry[] => {
    let section = sections.find(s => s.key === ref.key);
    if (!section) {
      section = {key: ref.key, titleKey: ref.titleKey, entries: []};
      sections.push(section);
    }
    return section.entries;
  };
  const empty: PreviewLabel[] = [];
  let identifier: string | null = null;
  let title: LivePreviewView['title'] = null;
  for (const line of lines) {
    if (line.kind === 'flat') {
      const slot = flatSlots.get(line)!;
      if (line.fieldId === IDENTIFICATION_NUMBER_TARGET && identifier === null && slot?.length) {
        identifier = slot[0].value;
      } else if (slot?.length) {
        entriesOf(line.section).push(previewEntry({text: line.label}, slot.map(entry => previewValue(entry)), []));
      } else if (slot) {
        empty.push({text: line.label});
      }
      continue;
    }
    const parts = cardParts.get(line)!;
    const values = parts.values.values;
    if (values === null) {
      continue;
    }
    const label: PreviewLabel = line.title ? {text: line.title} : {key: line.descriptor.titleKey, index: line.index};
    if (line.groupKind === 'title' && title === null && values.length) {
      title = cardTitle(line, parts.values);
    } else if (values.length) {
      entriesOf(line.section).push(previewEntry(label, [], [...line.header, ...parts.items]));
    } else {
      empty.push(label);
    }
  }
  const unplaced = answer.composed.filter(entry => unread.has(entry));
  const unplacedEntry = (entry: Composed) => previewEntry({text: entry.label}, [previewValue(entry)], []);
  const unplacedOnObject = unplaced.filter(entry => entry.on !== 'media');
  if (unplacedOnObject.length) {
    sections.push({key: UNPLACED_SECTION_KEY, titleKey: null, entries: unplacedOnObject.map(unplacedEntry)});
  }
  for (const entry of unplaced.filter(entry => entry.on === 'media')) {
    entriesOf(MEDIA_VALUES_SECTION).push(unplacedEntry(entry));
  }
  return {row, found: true, errors, identifier, title, sections, empty, lines: lineValues, cards};
}

/**
 * The parts of card `model` the preview reads off the page rather than the
 * answer: its picked headers - but the type header when the card is
 * `titled` by its type's name - each sub-block member's role and role
 * status, and the name `pickNames` (by the picked record's id) holds for
 * each fixed pick.
 */
export function previewCardParts(
  model: GroupCardModel, descriptor: GroupCardDescriptor, titled: boolean, pickNames: ReadonlyMap<string, string>,
): Pick<PreviewCardLine, 'header' | 'subBlocks' | 'pickNames'> {
  const typeHeader = titled ? descriptor.header.find(h => h.required) : undefined;
  const header: PreviewItem[] = [];
  for (const def of descriptor.header.filter(h => h !== typeHeader)) {
    const name = headerOptionName(def, model.headerControls[def.key]?.value);
    if (name) {
      header.push({label: {key: def.label}, values: [{text: name}]});
    }
  }
  const subBlocks = new Map<GroupMemberRow, PreviewSubBlock>();
  const names = new Map<GroupMemberRow, string>();
  for (const sub of model.subBlocks) {
    const def = descriptor.subBlocks.find(d => d.kind === sub.defKind);
    const picked = (key: string): string | null => {
      const headerDef = def?.header.find(h => h.key === key);
      return headerDef ? headerOptionName(headerDef, sub.headerControls[key]?.value) : null;
    };
    const role = picked('role_id');
    subBlocks.set(sub.member, {
      key: sub.key, label: role ? {text: role} : {key: def?.titleKey}, detail: picked('role_status_id'),
    });
    const member = sub.member;
    const pick = member.sourceControl.value === 'constant' && memberFixedIsPick(member, true)
      ? member.conceptControl.value : null;
    const name = pick && pickNames.get(pick);
    if (name) {
      names.set(member, name);
    }
  }
  return {header, subBlocks, pickNames: names};
}

/** A card's values and its labelled lines, as `readCard` reads them. */
interface CardParts {
  values: CardRowValues;
  items: PreviewItem[];
}

/** Card `card`'s values from `entries`, those the answer gave its key:
 * each member row's (`slotsByRow`) as `doubt` judges it, and a line per
 * member row with a value, labelled as the card labels the member
 * (`memberLabelInCard`). A member row an error leaves unknown leaves the
 * card's values unknown. */
function readCard(card: PreviewCardLine, entries: readonly Composed[], doubt: Doubt): CardParts {
  const slots = slotsByRow(card, entries);
  const textOf = (r: GroupMemberRow, entry: Composed) => card.pickNames.get(r) ?? entry.value;
  let doubted = false;
  const members = new Map<GroupMemberRow, RowValues>();
  const items: PreviewItem[] = [];
  const rowOf = new Map<Composed, GroupMemberRow>();
  for (const r of card.rows) {
    const read = slots.get(r)!;
    const slot = doubt(read, r.fieldId, card.lookupRows.has(r), 'card');
    doubted ||= slot === null && read !== null;
    members.set(r, slot && slot.map(entry => textOf(r, entry)));
    slot?.forEach(entry => rowOf.set(entry, r));
    if (slot?.length) {
      const sub = card.subBlocks.get(r);
      const label = memberLabel(card.descriptor, r) ?? slot[0].label;
      items.push({
        label: sub ? sub.label : {text: memberLabelInCard(card.descriptor, label)},
        values: slot.map(entry => previewValue(entry, card.pickNames.get(r))),
        ...(sub?.detail ? {detail: sub.detail} : {}),
      });
    }
  }
  const values = doubted ? null : entries.map(entry => {
    const r = rowOf.get(entry);
    return r ? textOf(r, entry) : entry.value;
  });
  return {values: {values, members}, items};
}

/** Each of `card`'s rows with its entries among `entries`: a sub-block
 * member row those with its `sub_block`, any other row those onto its
 * field that name no sub-block, in order (`inOrder`). */
function slotsByRow(card: PreviewCardLine, entries: readonly Composed[]): Map<GroupMemberRow, Slot> {
  const slots = new Map<GroupMemberRow, Slot>();
  for (const [fieldId, same] of groupBy(card.rows.filter(r => !card.subBlocks.has(r)), r => r.fieldId)) {
    const values = inOrder(same.length, entries.filter(entry => !entry.sub_block && entry.target_field === fieldId));
    same.forEach((r, i) => slots.set(r, values[i]));
  }
  for (const r of card.rows) {
    const sub = card.subBlocks.get(r);
    if (sub) {
      slots.set(r, entries.filter(entry => entry.sub_block === sub.key));
    }
  }
  return slots;
}

/** The label of the card's member field `r` maps onto. */
function memberLabel(descriptor: GroupCardDescriptor, r: GroupMemberRow): string | undefined {
  return descriptor.members.find(member => member.fieldId === r.fieldId)?.label;
}

/** `entry` as the preview shows it: a KulturNav objectid with a record
 * or page to link to by its entity (`kulturnavRefText`), else by `name`
 * when it is a fixed pick whose name is known; with where its terms land
 * (`termPath`). */
function previewValue(entry: Composed, name?: string): PreviewValue {
  const path = termPath(entry);
  const ref = entry.kulturnav;
  const linked = ref && (ref.artifact_id || ref.url) ? ref : undefined;
  return {
    text: linked ? kulturnavRefText(linked) : name ?? entry.value,
    ...(path ? {path} : {}),
    ...(linked ? {kulturnav: linked} : {}),
  };
}

/** Where `entry`'s terms land: each term that has ancestors, or a name
 * other than its piece of the value, as its ancestors and name;
 * `undefined` when that says nothing the value does not. */
function termPath(entry: Composed): string | undefined {
  const fold = (text: string) => text.trim().toLocaleLowerCase();
  const shown = (entry.terms ?? [])
    .filter(term => term.path || fold(term.name) !== fold(term.value))
    .map(term => term.path ? `${term.path} › ${term.name}` : term.name);
  return shown.length ? shown.join(' · ') : undefined;
}

/** `result`'s target field, lookup, group, media, incomplete-card,
 * rights and media pattern errors, a target field of one of `lines` apart
 * from the messages. */
function previewErrors(result: ImportModulePreflightResult, lines: readonly PreviewLine[]): PreviewErrors {
  const fields = lineFields(lines);
  return {
    fields: result.errors.target_fields.filter(error => fields.has(error)),
    messages: [
      ...result.errors.target_fields.filter(error => !fields.has(error)),
      ...result.errors.lookups, ...result.errors.groups, ...result.errors.media,
    ],
    incompleteCards: result.errors.incomplete_cards ?? [],
    lackingRights: result.errors.rights ?? [],
    mediaPattern: result.errors.media_pattern ?? null,
  };
}

function lineFields(lines: readonly PreviewLine[]): Set<string> {
  return new Set(lines.flatMap(line => line.kind === 'flat' ? [line.fieldId] : line.rows.map(r => r.fieldId)));
}

/** What a line writes onto: the row's object, a card, or the media the
 * row links. */
type LineTarget = 'object' | 'card' | 'media';

type Doubt = (slot: Slot, fieldId: string, readsLookup: boolean, on: LineTarget) => Slot;

/** The `rights` action for the right to set the created date and user. */
const CREATED_REVISION_RIGHT_ACTION = 'raw_import_created_revision';
const CREATED_REVISION_TARGETS: ReadonlySet<string> = new Set([CREATED_AT_TARGET, CREATED_BY_TARGET]);

/** What a line or member row onto `fieldId` gets once `result`'s errors
 * are taken into account: unknown where they touch it, else `slot`. */
function errorDoubt(result: ImportModulePreflightResult, errors: PreviewErrors): Doubt {
  const unresolved = new Set(errors.fields);
  const everyLine = result.errors.target_fields.length > errors.fields.length;
  const lookupsFailed = result.errors.lookups.length > 0;
  const groupsFailed = result.errors.groups.length > 0;
  const mediaFailed = result.errors.media.length > 0 || !!errors.mediaPattern;
  const createdRevisionRefused = errors.lackingRights.some(right => right.action === CREATED_REVISION_RIGHT_ACTION);
  return (slot, fieldId, readsLookup, on) => {
    if (slot === null || (readsLookup && lookupsFailed)) {
      return null;
    }
    const touched = everyLine || unresolved.has(fieldId) || (on === 'card' && groupsFailed)
      || (on === 'media' && mediaFailed) || (createdRevisionRefused && CREATED_REVISION_TARGETS.has(fieldId));
    return !slot.length && touched ? null : slot;
  };
}

/** The first row after `row` the Forhåndskontroll `result` found a warning
 * on; `null` when it found none past it. */
export function nextWarningRow(result: ImportModulePreflightResult | null, row: number): number | null {
  let next: number | null = null;
  for (const checked of result?.rows ?? []) {
    const number = Number(checked.row);
    if (checked.has_warnings && number > row && (next === null || number < next)) {
      next = number;
    }
  }
  return next;
}

/** The entries of a target the answer reports apart from its composed
 * values: the identification number, the created date and the created
 * by; `undefined` for any other field. */
function ownPartEntries(answer: ImportModulePreflightRow, fieldId: string): Slot | undefined {
  const entries = (part: {value: string} | null | undefined): Slot =>
    part?.value ? [{target_field: fieldId, label: '', value: part.value}] : [];
  switch (fieldId) {
    case IDENTIFICATION_NUMBER_TARGET:
      return entries(answer.id_number);
    case CREATED_AT_TARGET:
      return entries(answer.created_revision?.created_at);
    case CREATED_BY_TARGET:
      return entries(answer.created_revision?.created_by);
    default:
      return undefined;
  }
}

/** `count` like slots from `entries`: one entry each in order when there
 * are as many, none each when there is none, unknown otherwise. */
function inOrder(count: number, entries: readonly Composed[]): Slot[] {
  if (!entries.length) {
    return Array.from({length: count}, () => []);
  }
  return entries.length === count
    ? entries.map(entry => [entry])
    : Array.from({length: count}, () => null);
}

/** A title card's title - its title member's value, else its first value -
 * and its other values. */
function cardTitle(card: PreviewCardLine, values: CardRowValues): {text: string; details: string[]} {
  const all = values.values ?? [];
  const titleRow = card.rows.find(r =>
    card.descriptor.members.find(member => member.fieldId === r.fieldId)?.fieldName === 'title');
  const text = (titleRow && values.members.get(titleRow)?.[0]) ?? all[0];
  const details = [...all];
  details.splice(details.indexOf(text), 1);
  return {text, details};
}

function previewEntry(label: PreviewLabel, values: PreviewValue[], items: PreviewItem[]): PreviewEntry {
  const shown = [...values, ...items.flatMap(item => item.values)];
  return {
    label, values, items,
    wide: values.length > 1 || items.length > 1
      || shown.some(value => value.text.length > WIDE_VALUE_LENGTH || !!value.path),
  };
}

/** `items` sorted by `order`. */
function byOrder<T>(items: readonly T[], order: (item: T) => number): T[] {
  return [...items].sort((a, b) => order(a) - order(b));
}

/** `items` by `key`, in the order each key first appears. */
function groupBy<T, K>(items: readonly T[], key: (item: T) => K): Map<K, T[]> {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    groups.set(k, [...(groups.get(k) ?? []), item]);
  }
  return groups;
}
